import { computed, type Ref } from 'vue'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { NotificationMessage } from '../types'

dayjs.extend(customParseFormat)

export type NotificationTab = 'all' | 'unread'

export type NotificationGroupLabel = 'today' | 'yesterday' | 'older'

export interface NotificationGroup {
  label: NotificationGroupLabel
  items: NotificationMessage[]
}

export const useNotificationsByStatus = (
  notifications: Ref<NotificationMessage[]>,
  activeTab: Ref<NotificationTab>
) => {
  const unreadCount = computed(
    () => notifications.value.filter(item => item.status === 'UNREAD').length
  )

  const filteredNotifications = computed(() => {
    if (activeTab.value === 'unread') {
      return notifications.value.filter(item => item.status === 'UNREAD')
    }

    return notifications.value
  })

  const groupedNotifications = computed<NotificationGroup[]>(() => {
    const startOfToday = dayjs().startOf('day')
    const startOfYesterday = startOfToday.subtract(1, 'day')

    const groups: Record<NotificationGroupLabel, NotificationMessage[]> = {
      today: [],
      yesterday: [],
      older: [],
    }

    filteredNotifications.value.forEach(notification => {
      const createdAt = dayjs(notification.createdAt, 'DD.MM.YYYY HH:mm')

      if (createdAt.isSame(startOfToday, 'day')) {
        groups.today.push(notification)
      } else if (createdAt.isSame(startOfYesterday, 'day')) {
        groups.yesterday.push(notification)
      } else {
        groups.older.push(notification)
      }
    })

    return (['today', 'yesterday', 'older'] as NotificationGroupLabel[])
      .filter(label => groups[label].length > 0)
      .map(label => ({ label, items: groups[label] }))
  })

  return {
    unreadCount,
    groupedNotifications,
  }
}
